// src/components/admin/AdminNotifications.js
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

export default function AdminNotifications() {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) return;
    if (!window.confirm('Send this notification to all active users?')) return;
    setSending(true);
    setResult(null);
    try {
      const response = await fetch(
        `/api/send-manual-notification?secret=spocademy2026secure`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, message, adminId: currentUser?.uid })
        }
      );
      const data = await response.json();
      if (response.ok) {
        setResult({ success: true, totalSent: data.totalSent, totalFailed: data.totalFailed });
        setTitle('');
        setMessage('');
        setPreview(false);
        if (historyLoaded) loadHistory();
      } else {
        setResult({ success: false, error: data.error });
      }
    } catch (e) {
      setResult({ success: false, error: e.message });
    } finally {
      setSending(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { db } = await import('../../services/firebase/config');
      const { collection, getDocs, orderBy, query } = await import('firebase/firestore');
      const snap = await getDocs(query(collection(db, 'notificationHistory'), orderBy('sentAt', 'desc')));
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setHistoryLoaded(true);
    } catch (e) {
    } finally {
      setHistoryLoading(false);
    }
  };

  const getSortedHistory = () => {
    return [...history].sort((a, b) => {
      const at = a.sentAt?.seconds || 0;
      const bt = b.sentAt?.seconds || 0;
      return sortOrder === 'desc' ? bt - at : at - bt;
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return '—';
    return new Date(timestamp.seconds * 1000).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div>
      <div className="admin-section-title">Notifications</div>

      <div className="admin-card">
        <h3>Send Manual Notification</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          Sends to all active users with notifications enabled.
        </p>
        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Title (appears bold)</label>
          <input type="text" value={title}
            onChange={e => { setTitle(e.target.value); setPreview(false); setResult(null); }}
            placeholder="आजचा दिवस तुमचा आहे 💪" />
        </div>
        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Message (preserves line breaks)</label>
          <textarea value={message}
            onChange={e => { setMessage(e.target.value); setPreview(false); setResult(null); }}
            style={{ minHeight: '100px', resize: 'vertical' }}
            placeholder="सराव सुरू करा आणि आजचा दिवस जिंका!" />
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button className="admin-btn admin-btn-secondary"
            onClick={() => setPreview(p => !p)}
            disabled={!title.trim() || !message.trim()}>
            {preview ? 'Hide Preview' : 'Preview'}
          </button>
          <button className="admin-btn admin-btn-primary"
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim()}>
            {sending ? 'Sending...' : 'Send Now'}
          </button>
        </div>

        {preview && (
          <div style={{ background: '#1a1a2e', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <img src="/icons/icon-72x72.png" alt="icon"
                style={{ width: '40px', height: '40px', borderRadius: '8px', flexShrink: 0 }} />
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>{title}</div>
                <div style={{ color: '#ccc', fontSize: '14px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                  {message}
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className={`admin-status-msg ${result.success ? 'success' : 'error'}`}>
            {result.success
              ? `✓ Sent to ${result.totalSent} users.${result.totalFailed > 0 ? ` ${result.totalFailed} failed.` : ''}`
              : `Error: ${result.error}`}
          </div>
        )}
      </div>

      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>Notification History</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {historyLoaded && (
              <button className="admin-btn admin-btn-secondary"
                style={{ padding: '6px 12px', fontSize: '13px' }}
                onClick={() => setSortOrder(s => s === 'desc' ? 'asc' : 'desc')}>
                {sortOrder === 'desc' ? 'Oldest First' : 'Newest First'}
              </button>
            )}
            <button className="admin-btn admin-btn-secondary"
              style={{ padding: '6px 12px', fontSize: '13px' }}
              onClick={loadHistory} disabled={historyLoading}>
              {historyLoading ? 'Loading...' : historyLoaded ? 'Refresh' : 'Load History'}
            </button>
          </div>
        </div>

        {!historyLoaded && (
          <p style={{ color: '#999', fontSize: '14px' }}>Click "Load History" to view past notifications.</p>
        )}
        {historyLoaded && history.length === 0 && (
          <p style={{ color: '#999', fontSize: '14px' }}>No notifications sent yet.</p>
        )}
        {historyLoaded && getSortedHistory().map(item => (
          <div key={item.id} style={{ padding: '12px', marginBottom: '8px',
            background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '4px' }}>{item.title}</div>
                <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{item.message}</div>
              </div>
              <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>
                {formatDate(item.sentAt)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '13px' }}>
              <span style={{ color: '#28a745' }}>✓ {item.totalSent} sent</span>
              {item.totalFailed > 0 && (
                <span style={{ color: '#dc3545' }}>✗ {item.totalFailed} failed</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}